import type { RouteLocationNormalized, RouteRecordNormalized } from 'vue-router';
import type { App, Plugin } from 'vue';

import { unref } from 'vue';
import { isArray, isFunction, isObject } from '/@/utils/is';
import { find } from 'lodash';

export const noop = () => {};

/**
 * @description:  Set ui mount node
 */
export function getPopupContainer(node?: HTMLElement): HTMLElement {
  return (node?.parentNode as HTMLElement) ?? document.body;
}

/**
 * Add the object as a parameter to the URL
 * @param baseUrl url
 * @param obj
 * @returns {string}
 * eg:
 *  let obj = {a: '3', b: '4'}
 *  setObjToUrlParams('www.baidu.com', obj)
 *  ==>www.baidu.com?a=3&b=4
 */
export function setObjToUrlParams(baseUrl: string, obj: any): string {
  let parameters = '';
  for (const key in obj) {
    parameters += key + '=' + encodeURIComponent(obj[key]) + '&';
  }
  parameters = parameters.replace(/&$/, '');
  return /\?$/.test(baseUrl) ? baseUrl + parameters : baseUrl.replace(/\/?$/, '?') + parameters;
}

// 深度合并
export function deepMerge<T = any>(src: any = {}, target: any = {}): T {
  let key: string;
  for (key in target) {
    src[key] = isObject(src[key]) ? deepMerge(src[key], target[key]) : (src[key] = target[key]);
  }
  return src;
}

export function openWindow(
  url: string,
  opt?: { target?: TargetContext | string; noopener?: boolean; noreferrer?: boolean },
) {
  const { target = '__blank', noopener = true, noreferrer = true } = opt || {};
  const feature: string[] = [];

  noopener && feature.push('noopener=yes');
  noreferrer && feature.push('noreferrer=yes');

  window.open(url, target, feature.join(','));
}

// dynamic use hook props
export function getDynamicProps<T, U>(props: T): Partial<U> {
  const ret: Recordable = {};

  Object.keys(props).map((key) => {
    ret[key] = unref((props as Recordable)[key]);
  });

  return ret as Partial<U>;
}

export function getRawRoute(route: RouteLocationNormalized): RouteLocationNormalized {
  if (!route) return route;
  const { matched, ...opt } = route;
  return {
    ...opt,
    matched: (matched
      ? matched.map((item) => ({
          meta: item.meta,
          name: item.name,
          path: item.path,
        }))
      : undefined) as RouteRecordNormalized[],
  };
}

export const withInstall = <T>(component: T, alias?: string) => {
  const comp = component as any;
  comp.install = (app: App) => {
    app.component(comp.name || comp.displayName, component);
    if (alias) {
      app.config.globalProperties[alias] = component;
    }
  };
  return component as T & Plugin;
};

// 清除对象中的空值
export const clearEmpty = (obj: any) => {
  return Object.keys(obj).reduce((o, key) => {
    if (isObject(obj[key]) || isArray(obj[key])) {
      if (Object.keys(obj[key]).length > 0) {
        obj[key] = clearEmpty(obj[key]);
      } else {
        obj[key] = undefined;
      }
    }
    if (obj[key] !== undefined && obj[key] !== null && obj[key] !== '' && !isFunction(obj[key])) {
      o[key] = obj[key];
    }
    return o;
  }, new obj.constructor() as { [key: string]: any });
};

// 得到options中label
export const getValueLabel = (data, value) => {
  const result = find(data, { value });
  return result ? result.label : null;
};

// 全局拼接图片
export const interceptorImgUrl = (url: string) => {
  if (!url) return '';
  const prefix = import.meta.env.VITE_IMG_PREFIX;
  if (url.includes(prefix)) {
    const paths = interceptorImgUrl(url.replace(prefix, '')).split('/');
    if (!paths[1].includes('cilantro-md2')) {
      paths[1] = paths[1].replace('cilantro-md', 'cilantro-md2');
    }
    return `https://${paths[1]}.oss-cn-shenzhen.aliyuncs.com/${paths.slice(2).join('/')}`;
  }
  return url;
};

// 检测是否移动端设备
export function isMobileDevice() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}
